"use client"
import { useQualityLearning } from "@/hooks/use-quality-presentations"


import { Bell, Sparkles } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"
import { NOTIFICATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants"

import { weeklyRunDay } from "@/lib/conversation-quality/suggestions/checkpoints"
import type { NotificationChannelType } from "@/lib/notifications/types"
import { NOTIFICATION_CHANNELS_HREF } from "@/lib/notifications/page-link"
import type { QualitySuggestionsResult, SelfLearningSummaryResult } from "@/server-actions/conversation-quality"

import { LearnersSetting } from "../self-learning/learners-setting"
import { useTranslations } from "next-intl"


const CHANNEL_NAMES: Record<NotificationChannelType, string> = { email: "email", telegram: "Telegram" }

/** The top of the Learning tab: how the agents learn, and whether the weekly digest tells anyone. */
export function SelfLearningStrip({
  summary,
  digest,
}: {
  summary: SelfLearningSummaryResult | undefined;
  digest: QualitySuggestionsResult["digest"] | undefined;
}) {
  const { SELF_LEARNING_LABEL } = useQualityLearning()

  if (!summary) return <Skeleton className="h-32 w-full rounded-lg" />
  const { learners } = summary
  if (learners.scope === "agents" && learners.total === 0) return null

  return (
    <Card className="min-w-0 divide-y divide-violet-100 border-violet-200 dark:divide-violet-900/60 dark:border-violet-900">
      <div className="space-y-3 px-4 py-4 sm:px-6">
        <div className="flex items-center gap-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200">
            <Sparkles className="size-4" aria-hidden />
          </span>
          <h2 className="text-base font-semibold leading-6">{SELF_LEARNING_LABEL}</h2>
        </div>
        <LearnersSetting learners={learners} />
      </div>
      {digest && <DigestRow digest={digest} />}
    </Card>
  )
}

function DigestRow({ digest }: { digest: QualitySuggestionsResult["digest"] }) {
  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsSelfLearningStrip")

  const { can } = usePermissions()

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 sm:px-6">
      <Bell className="size-4 shrink-0 text-violet-700 dark:text-violet-300" aria-hidden="true" />
      <span className="text-sm font-medium"><span key={tUi("weeklyDigest")}>{tUi("weeklyDigest")}</span></span>
      <Badge
        variant="outline"
        className={
          digest.enabled
            ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
            : "text-muted-foreground"
        }
      >
        <span key={digest.enabled ? tUi("on") : tUi("off")}>{digest.enabled ? tUi("on") : tUi("off")}</span>
      </Badge>
      <span className="min-w-0 flex-1 basis-60 text-sm text-muted-foreground">{digestLine(digest)}</span>
      {can(NOTIFICATIONS_PERMISSIONS.MANAGE) && (
        <Button asChild variant="link" size="sm" className="h-auto px-0">
          <a href={NOTIFICATION_CHANNELS_HREF}><span key={tUi("change")}>{tUi("change")}</span></a>
        </Button>
      )}
    </div>
  )
}

function digestLine({ enabled, channels }: QualitySuggestionsResult["digest"]): string {
  if (!enabled) return "Nobody gets the weekly summary of what the agents learned and what waits for you"
  const day = weeklyRunDay()
  if (channels.length === 0) return `Goes out every ${day}, but no notification channel receives it yet`
  return `Every ${day} to ${channels.map((channel) => CHANNEL_NAMES[channel]).join(" and ")}, for weeks with conversations`
}
